/// <amd-module name="VSS/Platform/Date" />
import { dateToString, format } from "./String";
import {JustNow, AgoMinutes, AgoHours, Today, Yesterday, DayAtTimeFormat} from "VSS/Platform/Resources";
import { checkIfCustomerCurrentCultureExists } from "./Culture";

export const millisecond = 1;
export const second = millisecond * 1000;
export const minute = second * 60;
export const hour = minute * 60;
export const day = hour * 24;
export const week = day * 7;

/**
 * Enum for formats of ago representations of time.
 * @readonly
 * @enum {number}
 */
export const enum AgoFormat {
    /** The compact representation of time in ago format. */
    Compact,

    /** The extended representation time in ago format. */
    Extended
}

//
// Formatters used by the ago function.
//
interface IAgoFormatter {
    // Compact Short Format
    ctShortFormat?: Intl.DateTimeFormat;

    // Compact Long Format
    ctLongFormat?: Intl.DateTimeFormat;

    // Extended Week Format
    exWeekFormat?: Intl.DateTimeFormat;

    // Extended Month Format
    exMonthFormat?: Intl.DateTimeFormat;

    // Extended Year Format
    exYearFormat?: Intl.DateTimeFormat;

    // Time Format
    timeFormat?: Intl.DateTimeFormat;
}

const agoFormatters: { [locale: string]: IAgoFormatter } = {};

/**
 * @param date The Date object to format
 * @param format Date string format
 * @param dateTimeFormat Format to use if there is no cultureInfo
 * @return
 */
export function localeDateFormat(date: Date, format?: string, dateTimeFormat?: Intl.DateTimeFormat): string {
    if (checkIfCustomerCurrentCultureExists()) {
        return dateToString(date, true, format);
    }
    if (dateTimeFormat) {
        return dateTimeFormat.format(date);
    }

    return date.toLocaleDateString();
}

/**
 * @param date The Date object to format
 * @param format Date string format
 * @return
 */
export function dateformat(date: Date, format?: string, dateTimeFormat?: Intl.DateTimeFormat): string {
    if (checkIfCustomerCurrentCultureExists()) {
        return dateToString(date, false, format);
    }
    if (dateTimeFormat) {
        return dateTimeFormat.format(date);
    }

    return date.toLocaleDateString();
}

/**
 * Generate a string indicating how long ago the date is.
 *
 * @param date The Date object to format
 * @param agoFormat The required format
 * @param now The Dateobject of reference time
 * @param locale The current locale
 * @return A friendly string
 */
export function ago(date: Date, agoFormat?: AgoFormat, now?: Date, locale?: string): string {
    let agoFormatter = agoFormatters[locale || ""];
    if (!agoFormatter) {
        agoFormatters[locale || ""] = agoFormatter = {};
    }

    if (!now) {
        now = new Date();
    }

    if (!agoFormat) {
        agoFormat = AgoFormat.Compact;
    }

    // Getting the difference in milliseconds between now and the specified date
    const diff = now.getTime() - date.getTime();

    let result = "";

    if (diff < 2 * minute) {
        result = JustNow;
    } else if (agoFormat === AgoFormat.Compact) {
        if (diff < 59 * minute) {
            result = format(AgoMinutes, Math.round(diff / minute));
        } else if (now.getDate() === date.getDate() && now.getMonth() === date.getMonth() && now.getFullYear() === date.getFullYear()) {
            result = format(AgoHours, Math.round(diff / hour));
        } else if (diff < week && now.getDay() !== date.getDay()) {
            const yesterday = new Date();
            yesterday.setDate(now.getDate() - 1);
            if (date.getDate() === yesterday.getDate()) {
                result = Yesterday;
            } else {
                result = localeDateFormat(date, "ddd", new Intl.DateTimeFormat(locale, { weekday: "long" }));
            }
        } else if (now.getFullYear() === date.getFullYear()) {
            agoFormatter.ctShortFormat = agoFormatter.ctShortFormat || new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
            result = localeDateFormat(date, "m", agoFormatter.ctShortFormat);
        } else {
            agoFormatter.ctLongFormat =
                agoFormatter.ctLongFormat || new Intl.DateTimeFormat(locale, { month: "short", year: "numeric", day: "numeric" });
            result = localeDateFormat(date, "d", agoFormatter.ctLongFormat);
        }
    } else if (agoFormat === AgoFormat.Extended) {
        let dayPart = "";

        if (now.getDate() === date.getDate() && now.getMonth() === date.getMonth() && now.getFullYear() === date.getFullYear()) {
            dayPart = Today;
        } else if (diff < week && now.getDay() !== date.getDay()) {
            const yesterday = new Date();
            yesterday.setDate(now.getDate() - 1);
            if (date.getDate() === yesterday.getDate()) {
                dayPart = Yesterday;
            } else {
                agoFormatter.exWeekFormat = agoFormatter.exWeekFormat || new Intl.DateTimeFormat(locale, { weekday: "short" });
                dayPart = localeDateFormat(date, "ddd", agoFormatter.exWeekFormat);
            }
        } else if (now.getFullYear() === date.getFullYear()) {
            agoFormatter.exMonthFormat = agoFormatter.exMonthFormat || new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
            dayPart = localeDateFormat(date, "m", agoFormatter.exWeekFormat);
        } else {
            agoFormatter.exYearFormat =
                agoFormatter.exYearFormat || new Intl.DateTimeFormat(locale, { month: "short", year: "numeric", day: "numeric" });
                result = localeDateFormat(date, "d", agoFormatter.ctLongFormat);
        }

        agoFormatter.timeFormat = agoFormatter.timeFormat || new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "numeric" });
        const timePart = localeDateFormat(date, 't', agoFormatter.timeFormat);

        result = format(DayAtTimeFormat, dayPart, timePart);
    }

    if (!result) {
        result = date.toString();
    }

    return result;
}
